import { CheckOutlined, CopyOutlined } from '@ant-design/icons';
import { Dropdown, Tooltip } from 'antd';
import { ChevronDown } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { copyTable } from './utils/tableCopyUtils';
import './SmartTableComponent.css';

/**
 * SmartTableComponent — shared table wrapper used across chat, posts, the file
 * viewer, and inside RichCodeBlock's markdown preview. Mirrors the RichCodeBlock
 * design:
 *
 *   - One visible boundary (the outer rounded border)
 *   - Always-visible top toolbar: lowercase `table` label on the left, format
 *     chevron and copy button on the right
 *   - Horizontal row dividers inside the table; no vertical cell borders, no
 *     zebra stripes, no filled header cells
 *
 * Copy-as-rich-text is the default click on the copy icon (95% use case); the
 * chevron next to it opens a menu with Markdown and CSV variants.
 */
const SmartTableComponent = React.memo(({ children, isDarkMode, ...props }) => {
  const tableRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [copying, setCopying] = useState(false);

  const handleCopy = async (format = 'rich') => {
    if (!tableRef.current || copying) return;

    try {
      setCopying(true);
      const success = await copyTable(tableRef.current, format);

      if (success) {
        setCopied(true);
        setTimeout(() => {
          setCopied(false);
        }, 2000);
      }
    } catch (error) {
      console.error('Error copying table:', error);
    } finally {
      setCopying(false);
    }
  };

  const formatMenuItems = [
    {
      key: 'rich',
      label: (
        <div className="smart-table-copy-menu-item">
          <span className="format-name">Rich Text</span>
          <span className="format-desc">Best for Sheets/Excel</span>
        </div>
      ),
      onClick: () => handleCopy('rich'),
    },
    {
      key: 'markdown',
      label: (
        <div className="smart-table-copy-menu-item">
          <span className="format-name">Markdown</span>
          <span className="format-desc">For markdown editors</span>
        </div>
      ),
      onClick: () => handleCopy('markdown'),
    },
    {
      key: 'csv',
      label: (
        <div className="smart-table-copy-menu-item">
          <span className="format-name">CSV</span>
          <span className="format-desc">For data import</span>
        </div>
      ),
      onClick: () => handleCopy('csv'),
    },
  ];

  return (
    <div className={`smart-table-wrapper ${isDarkMode ? 'dark' : 'light'}`}>
      {/* Toolbar — always visible, mirrors the RichCodeBlock pattern. Label on
          the left, copy + format chooser on the right. No hover-reveal magic;
          users see controls immediately so they know they can copy. */}
      <div className="smart-table-toolbar">
        <span className="smart-table-label">table</span>
        <div className="smart-table-toolbar-actions">
          <Dropdown
            menu={{ items: formatMenuItems }}
            trigger={['click']}
            placement="bottomRight"
            disabled={copying}
          >
            <Tooltip title="Choose copy format">
              <button
                type="button"
                className="smart-table-btn smart-table-format-btn"
                aria-label="Select copy format"
                disabled={copying}
              >
                <ChevronDown size={14} strokeWidth={2} />
              </button>
            </Tooltip>
          </Dropdown>
          <Tooltip title={copied ? 'Copied!' : 'Copy table as Rich Text'}>
            <button
              type="button"
              className={`smart-table-btn smart-table-copy-btn ${copied ? 'copied' : ''}`}
              onClick={() => handleCopy('rich')}
              disabled={copying}
              aria-label="Copy table"
            >
              {copied ? <CheckOutlined /> : <CopyOutlined />}
            </button>
          </Tooltip>
        </div>
      </div>

      <div className="smart-table-scroll">
        <table ref={tableRef} {...props}>
          {children}
        </table>
      </div>
    </div>
  );
});

SmartTableComponent.displayName = 'SmartTableComponent';

export default SmartTableComponent;
